import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getDashboardLayout,
  putDashboardLayout,
  deleteDashboardLayout,
  dashboardLayoutQueryKey,
} from '../api/dashboardLayout';

export type LayoutSection = 'tiles' | 'charts';

export interface SectionLayout {
  order: string[];
  hidden: string[];
}

export interface DashboardLayout {
  version: 1;
  tiles: SectionLayout;
  charts: SectionLayout;
}

export interface DashboardLayoutDefaults {
  tileIds: string[];
  chartIds: string[];
}

export interface UseDashboardLayoutResult {
  layout: DashboardLayout;
  move: (section: LayoutSection, fromIndex: number, toIndex: number) => void;
  hide: (section: LayoutSection, id: string) => void;
  /**
   * Un-hide an id. With no `atIndex` it appends to the end of the order (the
   * Add-menu contract); a valid `atIndex` inserts at `min(atIndex,
   * order.length)` so Undo can put an item back where it was removed from.
   */
  show: (section: LayoutSection, id: string, atIndex?: number) => void;
  reset: () => void;
  isCustomized: boolean;
}

const STORAGE_PREFIX = 'dashboard-layout-v1-';

// Long enough to collapse a drag-reorder burst into one write, short enough
// that closing the tab rarely races a pending sync (which the unmount flush
// covers anyway).
const SYNC_DEBOUNCE_MS = 800;

// localStorage can throw (quota exceeded, Safari private mode, storage
// disabled). Persistence is best-effort: a failed write means the layout
// doesn't stick across reloads, never a crashed dashboard.
function safeSetItem(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // best-effort persistence: ignore
  }
}

function safeRemoveItem(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // best-effort persistence: ignore
  }
}

/** Shape guard for one stored section: both fields must be string arrays. */
function isSectionLayout(v: unknown): v is SectionLayout {
  if (typeof v !== 'object' || v === null) return false;
  const { order, hidden } = v as Record<string, unknown>;
  return (
    Array.isArray(order) &&
    order.every(id => typeof id === 'string') &&
    Array.isArray(hidden) &&
    hidden.every(id => typeof id === 'string')
  );
}

function defaultLayout(defaults: DashboardLayoutDefaults): DashboardLayout {
  return {
    version: 1,
    tiles: { order: [...defaults.tileIds], hidden: [] },
    charts: { order: [...defaults.chartIds], hidden: [] },
  };
}

/**
 * Reconcile a stored section against the current set of known ids:
 * unknown ids are dropped, duplicates within either list are collapsed to the
 * first occurrence, an id in both lists counts as hidden, and new ids are
 * appended to order (visible by default).
 */
export function reconcileSection(stored: SectionLayout, knownIds: string[]): SectionLayout {
  const known = new Set(knownIds);
  const seen = new Set<string>();
  const hidden: string[] = [];
  for (const id of stored.hidden) {
    if (known.has(id) && !seen.has(id)) {
      seen.add(id);
      hidden.push(id);
    }
  }
  const order: string[] = [];
  for (const id of stored.order) {
    if (known.has(id) && !seen.has(id)) {
      seen.add(id);
      order.push(id);
    }
  }
  for (const id of knownIds) {
    if (!seen.has(id)) {
      seen.add(id);
      order.push(id);
    }
  }
  return { order, hidden };
}

function loadLayout(tankId: string, defaults: DashboardLayoutDefaults): DashboardLayout {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + tankId);
    if (!raw) return defaultLayout(defaults);
    const parsed = JSON.parse(raw);
    if (parsed?.version !== 1) return defaultLayout(defaults);
    // A malformed section falls back to empty (reconcile then restores the
    // defaults for it) without discarding the other, still-valid section.
    const tiles: unknown = parsed.tiles;
    const charts: unknown = parsed.charts;
    return {
      version: 1,
      tiles: reconcileSection(
        isSectionLayout(tiles) ? tiles : { order: [], hidden: [] },
        defaults.tileIds
      ),
      charts: reconcileSection(
        isSectionLayout(charts) ? charts : { order: [], hidden: [] },
        defaults.chartIds
      ),
    };
  } catch {
    return defaultLayout(defaults);
  }
}

/**
 * Owns dashboard tile/chart order + hidden lists, persisted per tank.
 *
 * @param defaults Must be referentially stable — define it at module scope.
 *   The stored layout is only re-read when `tankId` changes; content changes
 *   to `defaults` without a `tankId` change are not picked up.
 */
export function useDashboardLayout(
  tankId: string | undefined,
  defaults: DashboardLayoutDefaults
): UseDashboardLayoutResult {
  const storageKey = STORAGE_PREFIX + (tankId ?? 'none');

  const [layout, setLayout] = useState<DashboardLayout>(() =>
    loadLayout(tankId ?? 'none', defaults)
  );

  // Tank switch: reset during render so the persist effect below never sees
  // the old tank's layout paired with the new tank's storage key.
  const [loadedKey, setLoadedKey] = useState(storageKey);
  if (loadedKey !== storageKey) {
    setLoadedKey(storageKey);
    setLayout(loadLayout(tankId ?? 'none', defaults));
  }

  const isCustomized = useMemo(() => {
    const def = defaultLayout(defaults);
    return JSON.stringify(layout) !== JSON.stringify(def);
  }, [layout, defaults]);

  // ---------------------------------------------------------------------
  // Server sync. localStorage already seeded the first render above, so the
  // server copy is a background reconcile: it makes layouts roam across
  // devices but is never load-bearing — every network path below swallows
  // failures, leaving the localStorage behavior as the floor.
  // ---------------------------------------------------------------------

  // With no tank there is nothing to sync against (the 'none' layout is a
  // purely local convenience) — the query stays disabled and the scheduling
  // below never runs, so an undefined tankId makes zero network calls.
  const serverQuery = useQuery({
    queryKey: dashboardLayoutQueryKey(tankId ?? 'none'),
    queryFn: () => getDashboardLayout(tankId!),
    enabled: !!tankId,
    staleTime: 60_000,
    retry: 1,
  });

  // True only between a *user* edit (move/hide/show) and the scheduling of
  // its debounced sync. Applying server data goes through setLayout without
  // touching this flag — that asymmetry is what makes PUT echo loops
  // impossible: a write can only ever originate from an edit.
  const dirtyRef = useRef(false);

  // The one captured-but-unsent sync. It snapshots tankId alongside the
  // layout so a flush after a tank switch still writes to the *old* tank.
  const pendingSyncRef = useRef<{
    tankId: string;
    layout: DashboardLayout;
    customized: boolean;
  } | null>(null);
  const syncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Latest layout/isCustomized for the server-apply effect, which must read
  // them at apply time without re-running on every edit.
  const layoutRef = useRef(layout);
  layoutRef.current = layout;
  const isCustomizedRef = useRef(isCustomized);
  isCustomizedRef.current = isCustomized;

  // Send the pending sync now, cancelling its debounce timer. A layout equal
  // to the defaults deletes the server row instead of storing a no-op copy —
  // the server-side mirror of the "remove key at defaults" rule below.
  //
  // The query cache is updated alongside every send: within staleTime a
  // revisited tank is served from cache, and a cache still holding the
  // pre-edit server copy would clobber the very edit this send persisted.
  const queryClient = useQueryClient();
  const flushPendingSync = useCallback(() => {
    if (syncTimeoutRef.current !== null) {
      clearTimeout(syncTimeoutRef.current);
      syncTimeoutRef.current = null;
    }
    const pending = pendingSyncRef.current;
    pendingSyncRef.current = null;
    if (!pending) return;
    // A GET for this key may still be in flight; left alone it would resolve
    // after the setQueryData below and replace the just-written entry with the
    // pre-edit server copy — which then looks freshest and gets re-applied on
    // the next mount within staleTime, visually reverting the edit. Cancelling
    // makes React Query discard the late result. Fire-and-forget is enough:
    // the abort is signalled synchronously, and the write below stamps a fresh
    // dataUpdatedAt so a refetch only becomes possible after staleTime — by
    // which point the server has long since seen the PUT/DELETE sent here.
    void queryClient.cancelQueries({ queryKey: dashboardLayoutQueryKey(pending.tankId) });
    if (pending.customized) {
      queryClient.setQueryData(dashboardLayoutQueryKey(pending.tankId), pending.layout);
      putDashboardLayout(pending.tankId, pending.layout).catch(() => {});
    } else {
      queryClient.setQueryData(dashboardLayoutQueryKey(pending.tankId), null);
      deleteDashboardLayout(pending.tankId).catch(() => {});
    }
  }, [queryClient]);

  // Apply the server result exactly once per tank load. Without the key
  // guard, later refetches (focus, staleTime expiry) would stomp edits made
  // since; last-write-wins only means the server wins at *load* time.
  const appliedServerKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!tankId || !serverQuery.isSuccess) return;
    if (appliedServerKeyRef.current === storageKey) return;
    appliedServerKeyRef.current = storageKey;
    const server = serverQuery.data;
    if (server) {
      // An edit made while the GET was in flight is newer than the server
      // snapshot — keep it (its own sync is already pending or scheduled).
      if (dirtyRef.current || pendingSyncRef.current) return;
      // Same reconcile path as localStorage loads: the server may hold ids
      // from a newer/older app version than this client knows about.
      setLayout({
        version: 1,
        tiles: reconcileSection(
          isSectionLayout(server.tiles) ? server.tiles : { order: [], hidden: [] },
          defaults.tileIds
        ),
        charts: reconcileSection(
          isSectionLayout(server.charts) ? server.charts : { order: [], hidden: [] },
          defaults.chartIds
        ),
      });
      // The persist effect below mirrors this to localStorage (or removes
      // the key when the server layout equals the defaults) without
      // scheduling a sync, because dirtyRef stays false.
    } else if (isCustomizedRef.current) {
      // First contact with an empty server while a local customization
      // exists: migrate it up once so the layout starts roaming. Cache the
      // migrated copy too, or every revisit within staleTime would see the
      // cached null and "migrate" again.
      queryClient.setQueryData(dashboardLayoutQueryKey(tankId), layoutRef.current);
      putDashboardLayout(tankId, layoutRef.current).catch(() => {});
    }
  }, [tankId, storageKey, serverQuery.isSuccess, serverQuery.data, defaults, queryClient]);

  // Tank switch or unmount: send any pending sync immediately (flush, not
  // cancel — dropping it would lose the last edit made before navigating).
  useEffect(() => {
    return () => {
      flushPendingSync();
      // A not-yet-scheduled edit from the old key must not be re-attributed
      // to the new tank. Realistically this only happens for an edit made in
      // the brief window before the tank id resolves (tankId undefined skips
      // scheduling, so the flag lingers); such an edit stays local-only.
      dirtyRef.current = false;
    };
  }, [tankId, flushPendingSync]);

  // Persist on change; remove key when back at defaults
  useEffect(() => {
    if (isCustomized) {
      safeSetItem(storageKey, JSON.stringify(layout));
    } else {
      safeRemoveItem(storageKey);
    }
    // A user edit additionally schedules the debounced server sync. The
    // snapshot captures customized-ness now; whether it becomes a PUT or a
    // DELETE is decided from that snapshot when the timer fires.
    if (dirtyRef.current && tankId) {
      dirtyRef.current = false;
      pendingSyncRef.current = { tankId, layout, customized: isCustomized };
      if (syncTimeoutRef.current !== null) clearTimeout(syncTimeoutRef.current);
      syncTimeoutRef.current = setTimeout(flushPendingSync, SYNC_DEBOUNCE_MS);
    }
  }, [layout, storageKey, isCustomized, tankId, flushPendingSync]);

  const move = useCallback((section: LayoutSection, fromIndex: number, toIndex: number) => {
    setLayout(prev => {
      // Guard negative indices: splice(-1, 1) would remove the LAST item, and
      // callers using indexOf get -1 on a miss.
      if (fromIndex < 0 || toIndex < 0) return prev;
      const order = [...prev[section].order];
      const [item] = order.splice(fromIndex, 1);
      if (item === undefined) return prev;
      order.splice(toIndex, 0, item);
      dirtyRef.current = true; // user edit → eligible for server sync
      return { ...prev, [section]: { ...prev[section], order } };
    });
  }, []);

  const hide = useCallback((section: LayoutSection, id: string) => {
    setLayout(prev => {
      // Guard like move(): an id that isn't currently visible — already
      // hidden, or unknown to this section — leaves nothing to hide, and a
      // fresh-but-equal object would dirty the layout and schedule a sync of
      // unchanged content (an unknown id would even be PUT, only to be
      // dropped by reconcile on the next load).
      if (!prev[section].order.includes(id)) return prev;
      dirtyRef.current = true; // user edit → eligible for server sync
      return {
        ...prev,
        [section]: {
          order: prev[section].order.filter(x => x !== id),
          hidden: prev[section].hidden.includes(id)
            ? prev[section].hidden
            : [...prev[section].hidden, id],
        },
      };
    });
  }, []);

  const show = useCallback((section: LayoutSection, id: string, atIndex?: number) => {
    setLayout(prev => {
      // Guard like move(): showing an id that is already visible (and not
      // hidden) changes nothing — returning prev keeps the object identity
      // and, crucially, skips the dirty flag so no spurious sync of unchanged
      // content gets scheduled.
      if (!prev[section].hidden.includes(id) && prev[section].order.includes(id)) return prev;
      dirtyRef.current = true; // user edit (incl. Undo) → eligible for server sync
      const hidden = prev[section].hidden.filter(x => x !== id);
      if (prev[section].order.includes(id)) {
        return { ...prev, [section]: { ...prev[section], hidden } };
      }
      const order = [...prev[section].order];
      // A valid atIndex inserts (clamped to the end); anything else — omitted,
      // negative, or fractional — falls back to append.
      const insertAt =
        atIndex !== undefined && Number.isInteger(atIndex) && atIndex >= 0
          ? Math.min(atIndex, order.length)
          : order.length;
      order.splice(insertAt, 0, id);
      return { ...prev, [section]: { order, hidden } };
    });
  }, []);

  const reset = useCallback(() => {
    // Reset supersedes any in-flight edit: cancel its pending sync outright
    // (flushing it would resurrect the just-discarded layout on the server)
    // and clear the server row so other devices fall back to defaults too.
    if (syncTimeoutRef.current !== null) {
      clearTimeout(syncTimeoutRef.current);
      syncTimeoutRef.current = null;
    }
    pendingSyncRef.current = null;
    dirtyRef.current = false;
    if (tankId) {
      // Same in-flight-GET race as flushPendingSync, with a worse ending: a
      // late GET result would overwrite the null below, a remount would then
      // resurrect the deleted layout into localStorage, and the next full
      // load would hit the migration path and PUT the dead layout back —
      // silently undoing the reset. Cancelling closes the whole chain at its
      // first link: the discarded result never re-enters the cache.
      void queryClient.cancelQueries({ queryKey: dashboardLayoutQueryKey(tankId) });
      queryClient.setQueryData(dashboardLayoutQueryKey(tankId), null);
      deleteDashboardLayout(tankId).catch(() => {});
    }
    setLayout(defaultLayout(defaults));
    safeRemoveItem(storageKey);
  }, [defaults, storageKey, tankId, queryClient]);

  return { layout, move, hide, show, reset, isCustomized };
}
